import { Alert, Button, Loader, Modal, NumberInput, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconFileTypePdf, IconFridge, IconPlus } from '@tabler/icons-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { formatEuros } from 'src/Components/Inventory/inventoryLabels';
import { PageHeader } from 'src/Components/Navigation/PageHeader';
import {
  openInventoryPdf,
  useRestoreInventoryItem,
  useUpdateInventorySettings,
} from 'src/Hooks/Mutations/useInventoryMutations';
import useFetchInventory from 'src/Hooks/Queries/useFetchInventory';
import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import { inventoryApi } from 'src/Services/API';
import { InventoryItemDto } from 'src/Services/API/generated';
import {
  trackInventoryExported,
  trackInventoryItemDeleted,
  trackInventoryViewed,
} from 'src/Services/Mixpanel/events';

import styles from './InventoryPage.module.scss';

const UNDO_DELAY_MS = 10_000;
const NO_ROOM = 'none';

interface DeletedState {
  deletedItem?: { id: string; name: string };
}

/** The inventory of the house, room by room. Values are only shown to managers. */
const InventoryPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const assetId = useCurrentAssetId();
  const { data, isLoading, isError } = useFetchInventory();
  const { mutate: restore } = useRestoreInventoryItem();
  const [deleted, setDeleted] = useState((location.state as DeletedState | null)?.deletedItem);
  const [exporting, setExporting] = useState(false);
  const [settingsOpened, setSettingsOpened] = useState(false);
  const undoTimer = useRef<number>();

  useEffect(() => {
    if (data)
      trackInventoryViewed({ item_count: data.items.length, is_manager: data.valuesVisible });
    // Once per visit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!data]);

  // « Annuler » stays 10 seconds after a deletion.
  useEffect(() => {
    if (!deleted) return;
    navigate(location.pathname, { replace: true, state: null });
    undoTimer.current = window.setTimeout(() => setDeleted(undefined), UNDO_DELAY_MS);
    return () => window.clearTimeout(undoTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleted?.id]);

  const byRoom = useMemo(() => {
    const groups = new Map<string, { name: string; items: InventoryItemDto[] }>();
    for (const item of data?.items ?? []) {
      const key = item.room?.id ?? NO_ROOM;
      const name = item.room?.name ?? t('inventory.add.noRoom', 'Sans pièce');
      const group = groups.get(key) ?? { name, items: [] };
      group.items.push(item);
      groups.set(key, group);
    }
    return [...groups.entries()]
      .sort(([keyA, a], [keyB, b]) =>
        keyA === NO_ROOM ? 1 : keyB === NO_ROOM ? -1 : a.name.localeCompare(b.name, 'fr')
      )
      .map(([key, group]) => ({ key, ...group }));
  }, [data, t]);

  const undo = () => {
    if (!deleted) return;
    window.clearTimeout(undoTimer.current);
    restore(deleted.id, {
      onSuccess: item => {
        trackInventoryItemDeleted({ undone: true });
        navigate(`/asset/${assetId}/inventories/${item.id}`);
      },
      onError: () => {
        notifications.show({
          color: 'red',
          message: t('inventory.undoError', "L'objet n'a pas pu être remis."),
        });
      },
    });
    setDeleted(undefined);
  };

  const exportPdf = async () => {
    if (!assetId) return;
    setExporting(true);
    try {
      const pdf = await openInventoryPdf(
        async () => (await inventoryApi.inventoryControllerExportAll(assetId)).data
      );
      trackInventoryExported({
        item_count: data?.items.length ?? 0,
        value_to_complete_count: pdf.valueToCompleteCount,
        scope: 'house',
      });
      notifications.show({
        color: 'teal',
        message: t(
          'inventory.export.done',
          'Le PDF est prêt. Une action « Envoyer l’inventaire à l’assurance » vous le rappellera dans un an.'
        ),
      });
    } catch {
      notifications.show({
        color: 'red',
        message: t('inventory.export.error', "Le PDF n'a pas pu être préparé. Réessayez."),
      });
    } finally {
      setExporting(false);
    }
  };

  if (isLoading) return <Loader />;
  if (isError || !data) {
    return <Text>{t('inventory.error', "Impossible d'afficher l'inventaire.")}</Text>;
  }

  const roomValue = (items: InventoryItemDto[]) =>
    items.reduce((sum, item) => sum + (item.currentValue ?? 0), 0);

  return (
    <div className={styles.container}>
      <PageHeader
        label={t('inventory.title', 'Inventaire de la maison')}
        rightElement={
          <Button
            size="md"
            leftSection={<IconPlus size={20} />}
            onClick={() => navigate(`/asset/${assetId}/inventories/new`)}
          >
            {t('inventory.add.cta', 'Ajouter un objet')}
          </Button>
        }
      />

      {deleted && (
        <div className={styles.undo}>
          <span>{t('inventory.deleted', '{{name}} supprimé', { name: deleted.name })}</span>
          <Button size="md" variant="white" onClick={undo}>
            {t('common.cancel', 'Annuler')}
          </Button>
        </div>
      )}

      {data.items.length === 0 ? (
        <div className={styles.empty}>
          <IconFridge size={40} />
          <Text className={styles.emptyTitle}>
            {t('inventory.empty.title', 'Un objet en moins d’une minute')}
          </Text>
          <Text className={styles.sub}>
            {t(
              'inventory.empty.text',
              'Choisissez la pièce, prenez l’objet en photo, dites quelques mots : Lucien remplit la fiche. Une fois par an, envoyez le dossier à votre assurance.'
            )}
          </Text>
          <Button
            size="lg"
            leftSection={<IconPlus size={22} />}
            onClick={() => navigate(`/asset/${assetId}/inventories/new`)}
          >
            {t('inventory.add.first', 'Ajouter le premier objet')}
          </Button>
        </div>
      ) : (
        <>
          <div className={styles.summary}>
            {data.valuesVisible && data.totalCurrentValue != null ? (
              <>
                <span className={styles.sub}>
                  {t('inventory.summary.worth', 'Vaut aujourd’hui, à peu près')}
                </span>
                <span className={styles.big}>{formatEuros(data.totalCurrentValue)}</span>
                <span className={styles.sub}>
                  {t('inventory.summary.replacement', '{{value}} à neuf', {
                    value: formatEuros(data.totalReplacementValue ?? 0),
                  })}
                  {' · '}
                  {t('inventory.summary.count', '{{count}} objets dans {{rooms}} pièces', {
                    count: data.items.length,
                    rooms: byRoom.length,
                  })}
                </span>
              </>
            ) : (
              <span className={styles.big}>
                {t('inventory.summary.countOnly', {
                  count: data.items.length,
                  defaultValue: '{{count}} objets',
                })}
              </span>
            )}
          </div>

          {data.valuesVisible && data.valueToCompleteCount > 0 && (
            <Alert color="orange">
              {t('inventory.summary.toComplete', {
                count: data.valueToCompleteCount,
                defaultValue:
                  '{{count}} objets sans valeur actuelle : il manque la valeur à neuf ou l’année d’achat.',
              })}
            </Alert>
          )}

          {data.valuesVisible && (
            <div className={styles.insurance}>
              <Text className={styles.sectionTitle}>
                {t('inventory.export.title', 'Dossier pour l’assurance')}
              </Text>
              <Text className={styles.sub}>
                {t(
                  'inventory.export.text',
                  'Un PDF avec chaque objet, sa photo, sa valeur à neuf et sa valeur actuelle, les totaux par pièce, et les factures en annexe. À envoyer une fois par an.'
                )}
              </Text>
              <Text size="sm" c="dimmed">
                {t('inventory.settings.rule', 'Usure : {{rate}} % par an, au plus {{max}} %.', {
                  rate: data.settings.depreciationRatePercent,
                  max: data.settings.depreciationMaxPercent,
                })}{' '}
                <Button
                  variant="transparent"
                  size="compact-sm"
                  onClick={() => setSettingsOpened(true)}
                >
                  {t('inventory.settings.change', 'Changer')}
                </Button>
              </Text>
              <Button
                size="md"
                leftSection={<IconFileTypePdf size={20} />}
                loading={exporting}
                onClick={exportPdf}
              >
                {t('inventory.export.cta', 'Préparer le PDF pour l’assureur')}
              </Button>
            </div>
          )}

          {byRoom.map(room => (
            <section key={room.key} className={styles.room}>
              <div className={styles.roomHeader}>
                <h2 className={styles.roomName}>{room.name}</h2>
                <span className={styles.sub}>
                  {t('inventory.objects', {
                    count: room.items.length,
                    defaultValue: '{{count}} objets',
                  })}
                  {data.valuesVisible ? ` · ${formatEuros(roomValue(room.items))}` : ''}
                </span>
              </div>
              {room.items.map(item => (
                <button
                  key={item.id}
                  type="button"
                  className={styles.item}
                  onClick={() => navigate(`/asset/${assetId}/inventories/${item.id}`)}
                >
                  {item.photos[0] ? (
                    <img
                      className={styles.thumb}
                      src={item.photos[0].previewThumbnailUrl ?? item.photos[0].url}
                      alt=""
                    />
                  ) : (
                    <span className={styles.thumb} />
                  )}
                  <span className={styles.itemText}>
                    <span className={styles.itemName}>
                      {item.quantity > 1 ? `${item.name} (×${item.quantity})` : item.name}
                    </span>
                    {item.brandAndModel && <span className={styles.sub}>{item.brandAndModel}</span>}
                  </span>
                  {item.valuesVisible && (
                    <span className={item.currentValue != null ? styles.value : styles.toComplete}>
                      {item.currentValue != null
                        ? formatEuros(item.currentValue)
                        : t('inventory.toComplete', 'à compléter')}
                    </span>
                  )}
                </button>
              ))}
            </section>
          ))}
        </>
      )}

      {data.exitedCount > 0 && (
        <Button
          variant="outline"
          size="md"
          onClick={() => navigate(`/asset/${assetId}/inventories/exited`)}
        >
          {t('inventory.exited.link', 'Objets sortis ({{count}})', { count: data.exitedCount })}
        </Button>
      )}

      <DepreciationModal
        opened={settingsOpened}
        onClose={() => setSettingsOpened(false)}
        rate={data.settings.depreciationRatePercent}
        max={data.settings.depreciationMaxPercent}
      />
    </div>
  );
};

/** The house wear rule: one place, for managers who know their contract. */
const DepreciationModal = ({
  opened,
  onClose,
  rate,
  max,
}: {
  opened: boolean;
  onClose: () => void;
  rate: number;
  max: number;
}) => {
  const { t } = useTranslation();
  const [values, setValues] = useState({ rate, max });
  const { mutate, isLoading } = useUpdateInventorySettings();
  useEffect(() => setValues({ rate, max }), [rate, max, opened]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t('inventory.settings.title', 'Règle d’usure de la maison')}
    >
      <div className={styles.modal}>
        <Text size="sm">
          {t(
            'inventory.settings.text',
            'Elle sert à calculer la valeur actuelle de chaque objet. Si votre contrat d’assurance en indique une, reprenez-la.'
          )}
        </Text>
        <NumberInput
          size="md"
          label={t('inventory.settings.rate', 'Usure par an')}
          suffix=" %"
          min={0}
          max={100}
          allowDecimal={false}
          value={values.rate}
          onChange={value => setValues(current => ({ ...current, rate: Number(value) || 0 }))}
        />
        <NumberInput
          size="md"
          label={t('inventory.settings.max', 'Usure au plus')}
          suffix=" %"
          min={0}
          max={100}
          allowDecimal={false}
          value={values.max}
          onChange={value => setValues(current => ({ ...current, max: Number(value) || 0 }))}
        />
        <div className={styles.modalActions}>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel', 'Annuler')}
          </Button>
          <Button
            loading={isLoading}
            onClick={() =>
              mutate(
                { depreciationRatePercent: values.rate, depreciationMaxPercent: values.max },
                {
                  onSuccess: onClose,
                  onError: () => {
                    notifications.show({
                      color: 'red',
                      message: t('inventory.settings.error', "La règle n'a pas pu être changée."),
                    });
                  },
                }
              )
            }
          >
            {t('common.save', 'Enregistrer')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default InventoryPage;
